import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { redrawLessonPicture } from '@platform/client';

import { useTrackedMutation } from '../actions/ActionsProvider';
import { describeApiError } from '../api-error';

import type { LessonWorkspace } from '@platform/schema/review';

const slotName = (position: number | undefined): string =>
  position === undefined ? 'the cover' : `scene ${String(position)}`;

/**
 * The picture slots a lesson is short of, each with a redraw at standard rate — GEN-237.
 * While its module's batch is out it offers none, so no slot is paid for twice.
 */
export function MissingPictures({
  lessonId,
  pictures,
  onChanged,
}: {
  lessonId: string;
  pictures: LessonWorkspace['pictures'];
  onChanged: () => Promise<void>;
}) {
  const redraw = useTrackedMutation('Redraw a picture', {
    mutationFn: async (position: number | undefined) =>
      redrawLessonPicture({
        path: { id: lessonId },
        body: position === undefined ? {} : { position },
        throwOnError: true,
      }),
    onSuccess: onChanged,
  });

  if (pictures.missing.length === 0) return null;

  return (
    <Alert severity={pictures.blocks ? 'error' : 'warning'}>
      <AlertTitle>Pictures</AlertTitle>
      <Typography variant="body2">
        {pictures.blocks
          ? 'This lesson can’t be published until every picture is drawn.'
          : 'Published before pictures were required, so it can still be published.'}
      </Typography>
      <Stack component="ul" spacing={0.5} sx={{ m: 0, mt: 1, pl: 2.5 }}>
        {pictures.missing.map(({ position, message }) => (
          <li key={message}>
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              <Typography variant="body2" sx={{ flex: 1 }}>
                {message}
              </Typography>
              {!pictures.pending && (
                <Button
                  size="small"
                  aria-label={`Redraw ${slotName(position)}`}
                  disabled={redraw.isPending}
                  onClick={() => {
                    redraw.mutate(position);
                  }}
                >
                  {redraw.isPending && redraw.variables === position
                    ? 'Drawing…'
                    : 'Redraw'}
                </Button>
              )}
            </Stack>
          </li>
        ))}
      </Stack>
      {redraw.isError && (
        <Typography variant="body2" sx={{ mt: 1 }}>
          {slotName(redraw.variables)} was not redrawn:{' '}
          {describeApiError(redraw.error)}
        </Typography>
      )}
    </Alert>
  );
}
